import {Placement} from '@popperjs/core';
import React, {FC, PropsWithChildren, ReactNode} from 'react';
import {connect} from 'react-redux';

import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {PaletteColorsEnum} from '../../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {updateSeenOnboardingTipsAction} from '../../../store/data/preferences/preferencesActions';
import {getSeenOnboardingTips} from '../../../store/data/preferences/preferencesSelectors';
import {ComplexTooltipContent} from '../tooltips/complexTooltipContent';

/*
 * Constants.
 */

// For some tips, we also want to store whether they have been seen in the user's preferences.
const tipsToSaveAsSeen = [
  OnboardingTipTypesEnum.DEMO_INBOX_TEAM_WORKSPACE,
  OnboardingTipTypesEnum.DEMO_INBOX_RULE,
  OnboardingTipTypesEnum.DEMO_INBOX_COMMENT,
  OnboardingTipTypesEnum.DEMO_INBOX_COMPOSER,
];

/*
 * Props.
 */

export interface InfoTipProps {
  className?: string;
  /** The tip title. */
  title: ReactNode;
  /** The tip description. */
  description: ReactNode;
  /** The onboarding tips that have already been seen. */
  seenOnboardingTips: ReadonlyArray<OnboardingTipTypesEnum>;
  /** Handler to save that a particular tip was seen. Will be invoked for tips types included in `tipsToSaveAsSeen`. */
  updateSeenOnboardingTips: HandlerOf<ReadonlyArray<OnboardingTipTypesEnum>>;
  /** The placement of the tip relative to the target. */
  placement?: Placement;
  /** An optional type. Tips with a type will be data-tracked. */
  tipType?: OnboardingTipTypesEnum;
  /** An optional image to display in the tip. */
  imageUrl?: string;
  /** A description to accompany the optional imageUrl. */
  imageDescription?: string;
  /** A conditional to stop the tip from rendering.  */
  shouldHide?: boolean;
}

/*
 * Component.
 */

const InfoTip: FC<PropsWithChildren<InfoTipProps>> = (props) => {
  const theme = useUserTheme();
  const {
    title,
    description,
    placement,
    imageUrl,
    imageDescription,
    shouldHide,
    tipType,
    children,
    seenOnboardingTips,
    updateSeenOnboardingTips,
  } = props;

  if (shouldHide) {
    return <>{children || null}</>;
  }

  const renderContent = () => (
    <ComplexTooltipContent
      maxWidth={240}
      title={title}
      body={description}
      imageUrl={imageUrl}
      imageDescription={imageDescription}
    />
  );

  const handleTracking = () => {
    if (tipType) {
      // Any tip that has a specified type should send a data tracking event when opened.
      trackInterfaceEvent(InterfaceEventsEnum.INFO_TIP_VIEW, {
        tip_type: tipType,
      });

      // If the tip should also be saved as seen in the user's preferences, do so.
      if (tipsToSaveAsSeen.includes(tipType)) {
        updateSeenOnboardingTips([tipType]);
      }
    }
  };

  // Return a tooltip coordinator, passing the children if they exist.
  // If no child is passed, a pink info icon will be rendered.
  return (
    <TooltipCoordinator
      className={props.className}
      render={renderContent}
      placement={placement}
      delay={200}
      isMultiline
      onRender={handleTracking}
      tooltipColor={theme.palette[PaletteColorsEnum.PINK].shade40}
      isInfoTip
      hasSeenInfoTip={tipType && seenOnboardingTips.includes(tipType)}
    >
      {children || null}
    </TooltipCoordinator>
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  seenOnboardingTips: getSeenOnboardingTips,
}));

export const InfoTipContainer = connect(mapStateToProps, {
  updateSeenOnboardingTips: updateSeenOnboardingTipsAction,
})(InfoTip);
